import React from 'react';
import Image from 'next/image';
import {MotionValue} from 'motion/react';
import {TestimonialDataItem} from '../../types/testimonial.types';
import {
    isMediaItem,
    isTestimonialItem,
    isVideoItem,
    isAvatarsOnlyItem,
} from '../../types/panel.types';
import {AvatarRow} from '../avatar-row/avatar-row.react-component';
import {Testimonial} from '../testimonial/testimonial.react-component';
import {Chip} from '../chip/chip/chip.react-component';
import VideoPlayer from '@/features/60dayhustle/components/video-player/video-player';
import styles from './social-proof-item.module.scss';

interface SocialProofItemProps {
    item: TestimonialDataItem;
    index: number;
    variant: 'desktop' | 'mobile';
    styles?: Record<string, string>;
    panelsRef?: React.MutableRefObject<HTMLDivElement[]>;
    scrollProgress?: MotionValue<number>;
}

export const SocialProofItem: React.FC<SocialProofItemProps> = ({
    item,
    index,
    variant,
    styles: externalStyles,
    panelsRef,
    scrollProgress,
}) => {
    const isMobile = variant === 'mobile';
    const itemStyles = externalStyles || styles;

    const handleRef = (el: HTMLDivElement | null) => {
        if (el && panelsRef && variant === 'desktop') {
            panelsRef.current[index] = el;
        }
    };

    // Media Item (Image)
    if (isMediaItem(item)) {
        const containerClass = isMobile
            ? itemStyles.mobileItem || styles.mobileItem
            : `${itemStyles.panel} ${itemStyles['panel-media']}`;

        const imageClass = isMobile
            ? itemStyles.mobileMediaImage || styles.mobileMediaImage
            : itemStyles['media-image'];

        const imageContent = (
            <Image
                className={imageClass}
                src={item.media.url}
                alt={item.media.alt}
                width={isMobile ? 400 : 100}
                height={isMobile ? 600 : 100}
                style={isMobile ? {height: 'auto', width: '100%'} : undefined}
            />
        );

        if (isMobile) {
            return (
                <div className={containerClass} key={index}>
                    <div className={itemStyles.imageContainer || styles.imageContainer}>
                        {imageContent}
                    </div>
                </div>
            );
        }

        return (
            <div className={containerClass} ref={handleRef} key={index}>
                {imageContent}
            </div>
        );
    }

    // Video Item
    if (isVideoItem(item)) {
        const containerClass = isMobile
            ? itemStyles.mobileItem || styles.mobileItem
            : `${itemStyles.panel} ${itemStyles['panel-video']}`;

        const videoClass = isMobile
            ? itemStyles.mobileMediaVideo || styles.mobileMediaVideo
            : itemStyles['media-video'];

        const videoContent = (
            <VideoPlayer
                videoSrc={item.media.url}
                className={videoClass}
                shouldTrackInteractions={true}
            />
        );

        if (isMobile) {
            return (
                <div className={containerClass} key={index}>
                    <div className={itemStyles.videoContainer || styles.videoContainer}>
                        {videoContent}
                        {item.chipType && (
                            <div
                                className={itemStyles.mobileChipOverlay || styles.mobileChipOverlay}
                            >
                                <Chip type={item.chipType} variant="inline" />
                            </div>
                        )}
                        {item.chipTypeBottom && (
                            <div
                                className={
                                    itemStyles.mobileChipOverlayBottom ||
                                    styles.mobileChipOverlayBottom
                                }
                            >
                                <Chip type={item.chipTypeBottom} variant="inline" />
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div className={containerClass} ref={handleRef} key={index}>
                {videoContent}
            </div>
        );
    }

    // Testimonial Item
    if (isTestimonialItem(item)) {
        if (isMobile) {
            return (
                <div className={itemStyles.mobileItem || styles.mobileItem} key={index}>
                    {item.avatars && (
                        <div className={itemStyles.mobileAvatarRow || styles.mobileAvatarRow}>
                            <AvatarRow avatars={item.avatars} scrollProgress={scrollProgress} />
                        </div>
                    )}
                    <div className={itemStyles.testimonialContainer || styles.testimonialContainer}>
                        <Testimonial
                            quote={item.testimonial.quote}
                            avatarImg={item.testimonial.author.avatar.url}
                            author={item.testimonial.author.name}
                        />
                    </div>
                </div>
            );
        }

        if (item.variant === 'full') {
            return (
                <div
                    className={`${itemStyles.panel} ${itemStyles['panel-testimonial-full']}`}
                    ref={handleRef}
                    key={index}
                >
                    <Testimonial
                        quote={item.testimonial.quote}
                        avatarImg={item.testimonial.author.avatar.url}
                        author={item.testimonial.author.name}
                        fullHeight={true}
                    />
                </div>
            );
        }

        if (item.variant === 'split') {
            const flexDirection = item.layout?.flexDirection || 'column';
            const isReversed = flexDirection === 'column-reverse';

            return (
                <div
                    className={`${itemStyles.panel} ${itemStyles['panel-testimonial-split']} ${
                        isReversed ? itemStyles['panel-reversed'] : ''
                    }`}
                    ref={handleRef}
                    key={index}
                >
                    {item.avatars && scrollProgress && (
                        <div className={itemStyles['avatar-row']}>
                            <AvatarRow avatars={item.avatars} scrollProgress={scrollProgress} />
                        </div>
                    )}
                    <Testimonial
                        quote={item.testimonial.quote}
                        avatarImg={item.testimonial.author.avatar.url}
                        author={item.testimonial.author.name}
                    />
                </div>
            );
        }
    }

    // Avatars Only Item
    if (isAvatarsOnlyItem(item)) {
        if (isMobile) {
            return (
                <div className={itemStyles.mobileItem || styles.mobileItem} key={index}>
                    <div className={itemStyles.mobileAvatarRow || styles.mobileAvatarRow}>
                        <AvatarRow avatars={item.avatars} scrollProgress={scrollProgress} />
                    </div>
                </div>
            );
        }
    }

    return null;
};
